import { Link } from "react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useActiveJobCounts } from "@/features/jobs";
import { queryKeys } from "@/lib/queryKeys";
import { fetchLandingPageStats } from "@/features/landing/api/landing";
import { LandingPlane } from "../components/LandingPlane";
import { TruthGate } from "../components/TruthGate";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface MetricProps {
  label: string;
  unit?: string;
  prefix?: string;
  value: number | null | undefined;
  loading: boolean;
  to: string;
  tip: string;
}

function Metric({ label, value, unit, prefix, loading, to, tip }: MetricProps) {
  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>
        <Link
          to={to}
          aria-label={`${label} — ${tip}`}
          className="group relative flex flex-col gap-1.5 border-l border-[hsl(var(--surface-instrument-fg)/0.12)] px-5 py-4 transition-colors first:border-l-0 hover:bg-[hsl(var(--surface-instrument-fg)/0.04)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--signal-orange)/0.5)] sm:border-l"
        >
          <div className="mono-meta text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--surface-instrument-fg)/0.55)]">
            {label}
          </div>
          <div className="flex items-baseline gap-1.5">
            <span
              className="tabular-nums text-3xl font-black leading-none text-[hsl(var(--surface-instrument-fg))] sm:text-4xl"
              style={{ fontFamily: "'Sora', 'Sora-fallback', ui-sans-serif, system-ui, sans-serif" }}
            >
              <TruthGate value={value} loading={loading} skeletonWidth={64}>
                {(v) => (
                  <>
                    {prefix ?? ""}
                    {Number(v).toLocaleString("en-US")}
                  </>
                )}
              </TruthGate>
            </span>
            {unit && (
              <span className="mono-meta text-sm text-[hsl(var(--surface-instrument-fg)/0.55)]">
                {unit}
              </span>
            )}
          </div>
          <div
            aria-hidden="true"
            className="absolute -bottom-px left-5 h-px w-8 transition-all duration-300 group-hover:w-16"
            style={{ background: "var(--gradient-thermal)" }}
          />
        </Link>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-[240px] text-xs leading-relaxed">
        {tip}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Landing v2 — Telemetry Strip
 *
 * Plane: instrument. Four live metrics, mono labels, tabular numerals,
 * TruthGate-protected values, no decorative iconography. Reuses the existing
 * `fetchLandingPageStats` + `useActiveJobCounts` data sources without
 * altering any contract.
 */
export function TelemetryStrip() {
  const { activeJobs, isLoading: countsLoading } = useActiveJobCounts();

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: queryKeys.landing.stats(),
    queryFn: fetchLandingPageStats,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const salaryK = stats?.avg_salary != null ? Math.round(stats.avg_salary / 1000) : null;

  return (
    <LandingPlane
      variant="instrument"
      aria-label="Platform telemetry, frozen at the archival snapshot"
      className="border-y border-[hsl(var(--surface-instrument-fg)/0.08)]"
    >
      <div className="container mx-auto px-4 py-6 sm:py-8">
        <div className="mono-meta mb-4 flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--surface-instrument-fg)/0.5)]">
          <span aria-hidden="true" className="inline-flex h-1.5 w-1.5 rounded-full bg-[hsl(var(--surface-instrument-fg)/0.45)]" />
          <span>SECTION 01 · TELEMETRY · FROZEN 18 AUG 2026</span>
        </div>
        <h2 className="sr-only">Platform telemetry, frozen at the archival snapshot (18 Aug 2026)</h2>

        <TooltipProvider delayDuration={150}>
          <div className="grid grid-cols-2 gap-y-2 sm:grid-cols-4 sm:gap-y-0" aria-live="polite" aria-atomic="false">
            <Metric
              label="Infrastructure roles indexed"
              value={activeJobs}
              loading={countsLoading}
              to="/jobs"
              tip="Postings that were active across all ingested sources on the final day. Frozen at the archival snapshot (18 Aug 2026)."
            />
            <Metric
              label="Companies indexed"
              value={stats?.total_companies ?? null}
              loading={statsLoading}
              to="/companies"
              tip="Unique employers indexed on the platform. Frozen at the archival snapshot (18 Aug 2026)."
            />
            <Metric
              label="Avg listed comp"
              value={salaryK}
              prefix="$"
              unit="K"
              loading={statsLoading}
              to="/jobs?sort=salary_high"
              tip="Mean of employer-provided salary ranges, rounded to the nearest thousand; excludes listings without disclosed pay. Frozen at the archival snapshot."
            />
            <Metric
              label="Roles added final week"
              value={stats?.new_this_week ?? null}
              loading={statsLoading}
              to="/jobs?sort=newest"
              tip="New roles indexed in the final seven days before the platform was archived."
            />

          </div>
        </TooltipProvider>
      </div>
    </LandingPlane>
  );
}
